import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, UserCheck } from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { apiClient } from '../lib/api';

type Me = {
  auth_user_id: string;
  advisor_id: string;
  organization_id: string;
  email: string | null;
  role: string;
};

/**
 * Guards the /business subtree.
 *
 * Three outcomes:
 *   - No Supabase session       → redirect to /login (remember where we came from)
 *   - Session, but /api/me 403  → "pending approval" screen (user is in Supabase Auth
 *                                  but no advisor row has been provisioned yet)
 *   - Session + /api/me 200     → render the protected subtree
 *
 * While the session is loading (first paint after a refresh) or /api/me is
 * in flight, render a spinner rather than a redirect — otherwise an authenticated
 * user would briefly see the login page on every hard reload.
 */
export function ProtectedRoute() {
  const { session, isLoading: authLoading } = useAuthStore();
  const location = useLocation();

  const meQuery = useQuery<Me, Error>({
    queryKey: ['me', session?.user.id],
    queryFn: () => apiClient.get<Me>('/me'),
    enabled: !!session,
    retry: (failureCount, err) => {
      // Don't retry 401/403 — those are terminal auth decisions, not transient failures
      const msg = err.message;
      if (msg.includes('401') || msg.includes('403')) return false;
      return failureCount < 2;
    },
    staleTime: 5 * 60_000,
  });

  if (authLoading) {
    return <FullScreenSpinner />;
  }

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (meQuery.isPending) {
    return <FullScreenSpinner />;
  }

  if (meQuery.isError) {
    const msg = meQuery.error.message;
    if (msg.includes('403')) {
      return <PendingApprovalScreen email={session.user.email ?? ''} />;
    }
    if (msg.includes('401')) {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return <ErrorScreen message={msg} />;
  }

  return <Outlet />;
}

function FullScreenSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center" dir="rtl">
      <Loader2 className="w-8 h-8 text-business-primary animate-spin" />
    </div>
  );
}

function PendingApprovalScreen({ email }: { email: string }) {
  const signOut = useAuthStore((s) => s.signOut);
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-navy-dark via-navy to-navy-light flex items-center justify-center px-4"
      dir="rtl"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-4">
          <UserCheck className="w-7 h-7 text-amber-600" />
        </div>
        <h1 className="text-xl font-bold text-navy mb-2">הבקשה שלך התקבלה</h1>
        <p className="text-sm text-slate-600 mb-1">
          החשבון שלך מזוהה במערכת, אך טרם אושר לשימוש.
        </p>
        {email && (
          <p className="text-sm font-medium text-navy mb-4" dir="ltr">
            {email}
          </p>
        )}
        <p className="text-xs text-slate-500 mb-6">
          צוות FAI360 יצור איתך קשר לאחר אישור הפרופיל כיועץ. בדרך כלל תוך יום עסקים.
        </p>
        <button
          type="button"
          onClick={() => signOut()}
          className="text-sm text-business-primary hover:underline"
        >
          יציאה
        </button>
      </div>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
      dir="rtl"
    >
      <div className="max-w-md bg-white rounded-xl shadow p-6 text-center">
        <h1 className="text-lg font-semibold text-navy mb-2">שגיאה בטעינת החשבון</h1>
        <p className="text-sm text-slate-600">{message}</p>
      </div>
    </div>
  );
}
